import { useCallback, useMemo } from 'react'

import { useAuthenticatedUser } from '~/hooks/useAuthentication'
import { useTranslation } from '~/src/hooks/useTranslation'

import { resolveShipmentTranslationKey } from '~/src/components/RyderShare/shared/terminology/resolveShipmentTranslationKey'

/**
 * Wrapper around useTranslation that rewrites shipment-domain keys to purchaseOrder.* for SGWS when those keys exist.
 * Call sites should pass explicit shipment.* / shipments.* / shipmentsTab.* paths; non-SGWS customers use keys unchanged.
 */
const useShipmentTranslation = (...args: Parameters<typeof useTranslation>) => {
  const [user] = useAuthenticatedUser()
  const { t: baseT, i18n, ready } = useTranslation(...args)

  const defaultNamespace: string | ReadonlyArray<string> =
    args[0] === undefined ? 'common' : (args[0] as string | ReadonlyArray<string>)

  const resolveKey = useCallback(
    (key: string) =>
      resolveShipmentTranslationKey(key, user.customer?.name, i18n, defaultNamespace),
    [defaultNamespace, i18n, user.customer?.name]
  )

  const t = useMemo(() => {
    const wrappedT = ((key: string, options?: Record<string, unknown> | string) => {
      const resolvedKey = resolveKey(key)

      if (typeof options === 'string') {
        return baseT(resolvedKey, options as never)
      }

      return baseT(resolvedKey, options as never)
    }) as typeof baseT

    return wrappedT
  }, [baseT, resolveKey])

  return { t, i18n, ready }
}

export default useShipmentTranslation
